import React from 'react';
import { useT } from '../../lib/lessonI18n';

// Schematic mid-sagittal section (face to the left). Tongue, lips and velum move with place/manner.
const TONGUE = {
  rest: 'M58,124 Q58,112 66,107 Q90,97 112,104 Q126,112 128,146',
  alveolar: 'M58,124 Q56,106 61,92 Q70,100 90,99 Q112,101 125,114 Q130,128 128,146',
  dental: 'M58,124 Q50,113 48,104 Q64,103 90,100 Q112,102 125,114 Q130,128 128,146',
  postalveolar: 'M58,124 Q60,108 68,89 Q80,90 94,94 Q113,100 125,114 Q130,128 128,146',
  palatal: 'M58,124 Q60,113 67,106 Q82,82 98,80 Q117,88 125,112 Q130,128 128,146',
  velar: 'M58,124 Q61,115 69,111 Q92,106 106,90 Q117,81 124,96 Q131,118 128,146',
  labiovelar: 'M58,124 Q61,115 69,111 Q92,106 106,90 Q117,81 124,96 Q131,118 128,146',
};
const TONGUE_FOR = { bilabial: 'rest', labiodental: 'rest', glottal: 'rest' };

export const PLACE_PT = {
  bilabial: [33, 105], labiodental: [44, 106], dental: [50, 103], alveolar: [60, 89], postalveolar: [70, 84],
  palatal: [90, 76], velar: [116, 80], labiovelar: [116, 80], glottal: [139, 164],
};

const NUM = { bilabial: 1, labiodental: 2, dental: 3, alveolar: 4, postalveolar: 5, palatal: 6, velar: 7, glottal: 8 };
const NUM_OFF = { bilabial: [-6, 2], labiodental: [-1, 9], dental: [4, 8], glottal: [0, 10] };

export const SagittalHead = ({ place, manner, voiced, labels = false, onPick, testid = 'sagittal-head', tongueD, rounded, jaw }) => {
  const T = useT();
  const tongue = tongueD || TONGUE[TONGUE_FOR[place] || place] || TONGUE.rest;
  const j = jaw == null ? 0 : jaw * 9;
  const nasal = manner === 'nasal';
  const lipsClosed = place === 'bilabial' && manner !== 'fricative';
  const round = rounded ?? (['labiovelar', 'postalveolar'].includes(place) || place === 'bilabial');
  const lower = jaw != null ? `M${round ? 27 : 32},${109 + j} Q${round ? 34 : 38},${round ? 104 + j : 107 + j} 42,${108 + j}` : lipsClosed ? 'M30,111 Q36,104 42,108' : place === 'labiodental' ? 'M34,113 Q42,106 48,108' : `M${round ? 30 : 32},116 Q38,112 42,114`;
  const upper = jaw != null && round ? 'M27,103 Q34,100 42,102' : 'M30,104 Q36,98 42,102';
  const velum = nasal ? 'M96,76 Q110,78 114,88 Q117,98 113,106' : 'M96,76 Q112,74 124,82 Q133,90 136,94';
  const pt = place && PLACE_PT[place];
  return (
    <svg viewBox="0 0 170 200" className="w-full h-full" data-testid={testid} role="img" aria-label={T('Sezione sagittale del tratto vocale', 'Sagittal section of the vocal tract')}>
      <defs>
        <radialGradient id="ctGlow"><stop offset="0%" stopColor="#fb923c" stopOpacity="0.95" /><stop offset="100%" stopColor="#fb923c" stopOpacity="0" /></radialGradient>
      </defs>
      {/* head profile */}
      <path d="M46,14 Q40,34 44,50 Q36,58 26,68 Q30,74 40,78 Q36,86 32,96 Q34,100 30,104 M30,118 Q34,128 38,134 Q44,146 70,150 Q92,152 104,160 L104,198"
        fill="none" stroke="rgba(148,163,184,0.55)" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M150,20 Q160,90 146,120 L150,198" fill="none" stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
      {/* nasal cavity */}
      <path d="M44,74 Q80,58 128,70 Q136,74 136,90" fill="rgba(34,211,238,0.05)" stroke="rgba(34,211,238,0.35)" strokeWidth="0.9" />
      {nasal && <path d="M132,96 Q130,74 100,66 Q70,62 40,72" fill="none" stroke="#22d3ee" strokeWidth="1.6" strokeDasharray="3 3" className="ct-flow" />}
      {/* palate: teeth → alveolar ridge → hard palate */}
      <path d="M50,102 L50,96 Q54,88 62,88 Q74,78 96,76" fill="none" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" />
      <path d="M48,96 L52,103 L54,96 Z" fill="#f8fafc" />
      <path d="M48,116 L52,109 L54,116 Z" fill="#f8fafc" style={{ transform: `translateY(${j * 0.7}px)`, transition: 'transform .5s ease' }} />
      {/* velum + uvula */}
      <path d={velum} fill="none" stroke="#f9a8d4" strokeWidth="2.4" strokeLinecap="round" style={{ transition: 'd .5s ease' }} />
      {/* pharynx wall */}
      <path d="M137,88 Q141,120 141,158" fill="none" stroke="rgba(148,163,184,0.6)" strokeWidth="1.4" />
      {/* lips */}
      <path d={upper} fill="none" stroke="#fda4af" strokeWidth="2.6" strokeLinecap="round" style={{ transition: 'd .5s ease' }} />
      <path d={lower} fill="none" stroke="#fda4af" strokeWidth="2.6" strokeLinecap="round" style={{ transition: 'd .4s ease' }} />
      {/* jaw */}
      <path d="M52,116 Q56,126 70,134 Q100,142 126,150" fill="none" stroke="rgba(148,163,184,0.35)" strokeWidth="1" />
      {/* tongue */}
      <path d={`${tongue} Q112,148 90,140 Q70,134 58,124 Z`} fill="rgba(244,114,182,0.35)" stroke="#f472b6" strokeWidth="2" strokeLinejoin="round" style={{ transition: 'd .5s cubic-bezier(.4,0,.2,1)' }} />
      {/* larynx */}
      <g transform="translate(139,164)">
        <rect x="-9" y="-4" width="18" height="8" rx="3" fill="rgba(15,23,42,0.9)" stroke="rgba(148,163,184,0.6)" strokeWidth="0.8" />
        <path d="M-7,0 L7,0" stroke={voiced ? '#a3e635' : '#64748b'} strokeWidth="1.6" className={voiced ? 'ct-buzz' : ''} />
      </g>
      {pt && <circle cx={pt[0]} cy={pt[1]} r="9" fill="url(#ctGlow)" className="ct-pulse" />}
      {labels && Object.entries(PLACE_PT).filter(([k]) => k !== 'labiovelar').map(([k, [x, y]]) => (
        <g key={k} onClick={onPick ? () => onPick(k) : undefined} className={onPick ? 'cursor-pointer' : ''} data-testid={`sagittal-pt-${k}`}>
          <circle cx={x} cy={y} r={place === k ? 2.6 : 1.8} fill={place === k ? '#fb923c' : '#22d3ee'} />
          <text x={x + (NUM_OFF[k]?.[0] ?? 0)} y={y + (NUM_OFF[k]?.[1] ?? -4)} fontSize="5.5" fontWeight="800" textAnchor="middle" fill="#fdba74">{NUM[k]}</text>
        </g>
      ))}
    </svg>
  );
};
